import React from "react";
import { useBreadcrumbs } from "contexts/BreadcrumbsContext";
import HealthHeader from "./components/HealthHeader";
import HealthTable from "./components/HealthTable";
import {
    fetchCustomerHealth,
    CustomerHealthRow,
    RuleKey,
    SortField,
    SortDir,
    downloadCustomerHealthExcel,
    fetchAccountabilityPeople,
    AccountabilityPerson,
} from "./api/api";
import { useAuth } from "contexts/AuthContext";

const CustomerHealthPage: React.FC = () => {
    const { setBreadcrumbs } = useBreadcrumbs();

    const [search, setSearch] = React.useState<string>("");
    const [debouncedSearch, setDebouncedSearch] = React.useState<string>("");
    const [redRules, setRedRules] = React.useState<Set<RuleKey>>(new Set());
    const [accountabilityPeople, setAccountabilityPeople] = React.useState<AccountabilityPerson[]>([]);
    const [accountabilityEcode, setAccountabilityEcode] = React.useState<string>("");

    const [rows, setRows] = React.useState<CustomerHealthRow[]>([]);
    const [total, setTotal] = React.useState<number>(0);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);

    const [page, setPage] = React.useState<number>(1);
    const [pageSize, setPageSize] = React.useState<number>(20);
    const [sortBy, setSortBy] = React.useState<SortField>("red_count");
    const [sortOrder, setSortOrder] = React.useState<SortDir>("desc");

    const { permissions } = useAuth();
    const [downloadingExcel, setDownloadingExcel] = React.useState<boolean>(false);

    const canDownloadExcel = React.useMemo(() => {
        return Array.isArray(permissions) && permissions.includes("customer.health.download");
    }, [permissions]);

    const canViewAllAccountability = React.useMemo(() => {
        return Array.isArray(permissions) && permissions.includes("customer.health.view");
    }, [permissions]);

    React.useEffect(() => {
        setBreadcrumbs([
            { label: "Dashboard", href: "/dashboard/customer-health" },
            { label: "Customer Health" },
        ]);
    }, [setBreadcrumbs]);

    React.useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    React.useEffect(() => {
        (async () => {
            try {
                const resp = await fetchAccountabilityPeople();
                setAccountabilityPeople(resp.items);

                // Auto-select self if user cannot view all
                if (!canViewAllAccountability && resp.items.length > 0) {
                    setAccountabilityEcode(resp.items[0].ecode);
                }
            } catch (err) {
                console.error("Failed to load accountability people", err);
            }
        })();
    }, [canViewAllAccountability]);

    const handleAccountabilityChange = React.useCallback((ecode: string) => {
        setAccountabilityEcode(ecode);
        setPage(1);
    }, []);

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const resp = await fetchCustomerHealth({
                    search: debouncedSearch || undefined,
                    red_rules: Array.from(redRules),
                    sort: { by: sortBy, dir: sortOrder },
                    page,
                    page_size: pageSize,
                    final_accountability_ecode: accountabilityEcode || undefined,
                });
                if (cancelled) return;
                setRows(resp.items);
                setTotal(resp.total);
            } catch (err: any) {
                if (cancelled) return;
                setRows([]);
                setTotal(0);
                setError(err?.data?.detail || err?.message || "Something went wrong");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [debouncedSearch, redRules, sortBy, sortOrder, page, pageSize, accountabilityEcode]);

    const handleDownloadExcel = React.useCallback(async () => {
        try {
            setDownloadingExcel(true);

            const blob = await downloadCustomerHealthExcel({
                search: debouncedSearch || undefined,
                red_rules: Array.from(redRules),
                sort: { by: sortBy, dir: sortOrder },
                page,
                page_size: pageSize,
                final_accountability_ecode: accountabilityEcode || undefined,
            });

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");

            a.href = url;
            a.download = "Customer_Health.xlsx";
            document.body.appendChild(a);
            a.click();
            a.remove();

            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Customer health Excel download failed", err);
        } finally {
            setDownloadingExcel(false);
        }
    }, [debouncedSearch, redRules, sortBy, sortOrder, page, pageSize, accountabilityEcode]);

    const handleToggleRule = React.useCallback((rule: RuleKey) => {
        setRedRules((prev) => {
            const next = new Set(prev);
            if (next.has(rule)) next.delete(rule);
            else next.add(rule);
            return next;
        });
        setPage(1);
    }, []);

    const handleClearRules = React.useCallback(() => {
        setRedRules(new Set());
        setPage(1);
    }, []);

    const handleSortChange = React.useCallback(
        (col: SortField) => {
            setPage(1);
            if (sortBy === col) {
                setSortOrder((p) => (p === "asc" ? "desc" : "asc"));
            } else {
                setSortBy(col);
                setSortOrder("asc");
            }
        },
        [sortBy]
    );

    return (
        <div className="flex flex-col h-full gap-4 px-4 sm:px-6 overflow-hidden">
            <HealthHeader
                search={search}
                redRules={redRules}
                total={total}
                onSearchChange={setSearch}
                onToggleRule={handleToggleRule}
                onClearRules={handleClearRules}
                showDownloadButton={canDownloadExcel}
                downloadingExcel={downloadingExcel}
                onDownloadExcel={handleDownloadExcel}
                accountabilityPeople={accountabilityPeople}
                selectedAccountability={accountabilityEcode}
                onAccountabilityChange={handleAccountabilityChange}
                disableAccountabilityDropdown={!canViewAllAccountability}
            />

            {error ? (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-red-600 text-sm">{error}</p>
                </div>
            ) : (
                <HealthTable
                    data={rows}
                    loading={loading}
                    page={page}
                    pageSize={pageSize}
                    totalItems={total}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSortChange={handleSortChange}
                    onPageChange={setPage}
                    onPageSizeChange={(s) => {
                        setPage(1);
                        setPageSize(s);
                    }}
                />
            )}
        </div>
    );
};

export default CustomerHealthPage;
